import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import authService from '../services/authService';

// Auth state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isInitialized: false,
};

// Auth actions
const AUTH_ACTIONS = {
  INITIALIZE_START: 'INITIALIZE_START',
  INITIALIZE_SUCCESS: 'INITIALIZE_SUCCESS',
  INITIALIZE_FAILURE: 'INITIALIZE_FAILURE',
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LOADING: 'SET_LOADING',
};

// Auth reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.INITIALIZE_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
      
    case AUTH_ACTIONS.INITIALIZE_SUCCESS:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        isInitialized: true,
        error: null,
      };
      
    case AUTH_ACTIONS.INITIALIZE_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        error: null, // Don't show initialization errors to user
      };
      
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };
      
    case AUTH_ACTIONS.LOGIN_SUCCESS:
    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
      
    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
      
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false,
        isInitialized: true,
      };
      
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
      
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
      
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
      
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext();

// Auth provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize authentication on app load
  const initialize = useCallback(async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.INITIALIZE_START });
      
      const user = await authService.initializeAuth();
      dispatch({ 
        type: AUTH_ACTIONS.INITIALIZE_SUCCESS, 
        payload: user 
      });
      
      // Dispatch custom event for other services
      if (user) {
        window.dispatchEvent(new CustomEvent('auth:login', { detail: user }));
      }
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.INITIALIZE_FAILURE, 
        payload: error.message 
      });
    }
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      
      const result = await authService.login(email, password);
      
      dispatch({ 
        type: AUTH_ACTIONS.LOGIN_SUCCESS, 
        payload: result.user 
      });
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('auth:login', { detail: result.user }));
      
      return result;
    } catch (error) {
      dispatch({ 
        type: AUTH_ACTIONS.LOGIN_FAILURE, 
        payload: error.message 
      });
      throw error;
    }
  }, []);

  // Register function
  const register = useCallback(async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.REGISTER_START });
      
      const result = await authService.register(userData);
      
      dispatch({ 
        type: AUTH_ACTIONS.REGISTER_SUCCESS, 
        payload: result.user 
      });
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('auth:login', { detail: result.user }));
      
      return result;
    } catch (error) {
      dispatch({ 
        type: AUTH_ACTIONS.REGISTER_FAILURE, 
        payload: error.message 
      });
      throw error;
    }
  }, []);

  // Google login function
  const googleLogin = useCallback(async (credential) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });

      const result = await authService.googleLogin(credential);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: result.user,
      });

      window.dispatchEvent(new CustomEvent('auth:login', { detail: result.user }));

      return result;
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message,
      });
      throw error;
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('auth:logout'));
    } catch (error) {
      // Force logout even if API call fails
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  }, []);

  // Update user data
  const updateUser = useCallback(async (updates) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      
      // Update user on server if needed
      const updatedUser = await authService.getCurrentUser();
      
      dispatch({ 
        type: AUTH_ACTIONS.UPDATE_USER, 
        payload: updatedUser 
      });
      
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      
      return updatedUser;
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      throw error;
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const user = await authService.getCurrentUser();
      dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: user });
      return user;
    } catch (error) {
      throw error;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  // Change password
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      
      // Password change successful, user needs to login again
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      window.dispatchEvent(new CustomEvent('auth:logout'));
      
      return true;
    } catch (error) {
      throw error;
    }
  }, []);

  // Deactivate account
  const deactivateAccount = useCallback(async (password) => {
    try {
      await authService.deactivateAccount(password);

      // Account deactivated, logout user
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      window.dispatchEvent(new CustomEvent('auth:logout'));

      return true;
    } catch (error) {
      throw error;
    }
  }, []);

  // Resend verification email
  const resendVerificationEmail = useCallback(async (email) => {
    try {
      await authService.resendVerificationEmail(email);
      return true;
    } catch (error) {
      throw error;
    }
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Listen for auth events from other parts of the app
  useEffect(() => {
    const handleAuthLogout = () => {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []);

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    return state.user?.role === role;
  }, [state.user]);

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback((roles) => {
    return roles.includes(state.user?.role);
  }, [state.user]);

  const value = {
    // State
    ...state,

    // Actions
    login,
    googleLogin,
    register,
    logout,
    updateUser,
    refreshUser,
    clearError,
    changePassword,
    deactivateAccount,
    resendVerificationEmail,

    // Utilities
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

// HOC for components that require authentication
export function withAuth(Component) {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();
    
    if (isLoading) {
      return <div>Loading...</div>; // Replace with your loading component
    }
    
    if (!isAuthenticated) {
      return <div>Please log in to access this page.</div>; // Replace with your login redirect
    }
    
    return <Component {...props} />;
  };
}

// Hook for protected routes
export function useRequireAuth() {
  const { isAuthenticated, isLoading, isInitialized } = useAuth();
  
  return {
    isAuthenticated,
    isLoading: !isInitialized || isLoading,
    shouldRedirect: isInitialized && !isLoading && !isAuthenticated,
  };
}